import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Overline } from "@/components/Bits";
import CheckoutDialog from "@/components/CheckoutDialog";
import { ShoppingBag } from "lucide-react";

const CATEGORIES = ["All", "Leadership", "Christian Living", "Devotional", "Business", "AI & Innovation"];

export default function Books() {
  const [books, setBooks] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/products", { params: { type: "book" } }).then(({ data }) => setBooks(data)).catch(() => {});
  }, []);

  const shown = filter === "All" ? books : books.filter((b) => b.category === filter);

  return (
    <div data-testid="books-page">
      <PageHero
        overline="Books & Publications"
        title={<>Wisdom for <span className="font-accent text-gold-gradient italic">every generation.</span></>}
        subtitle="A growing collection of books, devotionals, leadership guides, and Kingdom resources written to inspire faith, leadership, and transformational living."
      />

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-24">
        <Reveal className="mb-12 flex flex-wrap items-center gap-3">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`book-filter-${c.toLowerCase().replace(/\s|&/g, "-")}`}
              className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${
                filter === c ? "border-gold-400 text-gold-300 bg-gold-500/10" : "border-white/15 text-white/60 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </Reveal>

        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((b, i) => (
            <Reveal key={b.slug} delay={(i % 3) * 0.06}>
              <div className="group flex h-full flex-col" data-testid={`book-${b.slug}`}>
                <div className="relative overflow-hidden border border-gold-500/20 aspect-[3/4] bg-ink-paper">
                  <img src={b.image} alt={b.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 ring-1 ring-inset ring-white/5" />
                </div>
                <span className="mt-5 text-[10px] uppercase tracking-[0.25em] text-gold-500">{b.category}</span>
                <h3 className="mt-2 font-serif text-2xl text-white">{b.title}</h3>
                <p className="mt-3 text-sm text-white/60 leading-relaxed flex-1">{b.description}</p>
                <div className="mt-6 flex items-center justify-between">
                  <span className="font-serif text-2xl text-gold-400">${b.price}</span>
                  <CheckoutDialog item={{ type: "product", slug: b.slug, title: b.title, price: b.price }}>
                    <button data-testid={`buy-${b.slug}`} className="inline-flex items-center gap-2 bg-gold-500 px-5 py-2.5 text-xs font-semibold text-ink hover:bg-gold-400 transition-colors duration-300">
                      <ShoppingBag size={15} /> Buy
                    </button>
                  </CheckoutDialog>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
